import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectActiveCapabilities } from 'store/userGroups';
import {
  selectIntegrationById,
  selectIntegrationsAreFetched
} from 'store/integrations';
import { Booking, IntegrationID } from '__types__';
import { CapabilityEnum } from 'dtos/constants/capability.enum';
import { selectActiveAccountId } from 'store/session';
import { checkOverallAccess } from '../../../../../lib/restrictedAccess/accessCheckers';

/**
 * Custom hook that determines if the current user has access to visitor management
 * for a specific booking. Access is granted if:
 * 1. User has MANAGE_VISITORS capability, OR
 * 2. User is the owner of the booking
 * AND
 * 3. Visitor management integration is enabled
 */
export const useVisitorManagementAccess = (booking: Booking | null) => {
  const currentUserId = useSelector(selectActiveAccountId);
  const activeCapabilities = useSelector(selectActiveCapabilities);
  const integrationsFetched = useSelector(selectIntegrationsAreFetched);
  const visitorManagementIntegration = useSelector((state: any) =>
    selectIntegrationById(state, IntegrationID.VISITOR_MANAGEMENT)
  );

  return useMemo(() => {
    if (!booking || !currentUserId) {
      return false;
    }

    if (!integrationsFetched) {
      return false;
    }

    if (!visitorManagementIntegration?.enabled) {
      return false;
    }

    const hasVisitorCapability = checkOverallAccess(
      null,
      [],
      [CapabilityEnum.MANAGE_VISITORS, CapabilityEnum.VIEW_VISITORS],
      [],
      activeCapabilities
    );

    const isOwner = booking.user_id === currentUserId;

    return hasVisitorCapability || isOwner;
  }, [
    booking,
    currentUserId,
    activeCapabilities,
    integrationsFetched,
    visitorManagementIntegration
  ]);
};
